import { Box, CssBaseline } from '@mui/material';
import { ThemeProvider as V5MuiThemeProvider } from '@mui/material/styles';
import React, { lazy, Suspense } from 'react';
import { Navigate, NavigateProps, useRoutes } from 'react-router-dom';

import { routes } from '../routes';
import AccessDeniedPage from './access-denied-page';
import * as Auth from './auth';
import MagicLogin from './auth/magic-login';
import AuthRoute from './auth-route';
import { getDesignTokens } from './base/core';
import { createAppTheme } from './base/mui-theme';
import { NotistackProvider } from './base/notistack-snackbar/notistack-provider';
import DashboardDemo from './dashboard-demo';
import DevDemo from './dev-demo';
import ErrorPage from './error-page';
import FloatingTaskButton from './floating-task-button';
import { PrivacyPolicy } from './public-content';
import TestAgents from './public-content/test-agent';
import { PUBLIC_ROUTES } from './public-routes';
import SignIn2faVerification from './settings/2fa-auth';

import { OrganizationProjectProvider } from '~/client/components/organization-context';
import { SessionWarningModal } from '~/client/components/session/session-warning-modal';
import {
  AccentProvider,
  useAccentPalette,
} from '~/client/contexts/accent-context';
import { AnchProvider } from '~/client/contexts/anch-context';
import { NotificationProvider } from '~/client/contexts/notification-context';
import { PermissionsProvider } from '~/client/contexts/permissions-context';
import { SessionProvider } from '~/client/contexts/session-context';
import { ThemeProvider, useThemeMode } from '~/client/contexts/theme-context';
import { OrganizationBrandingProvider } from '~/client/hooks/use-organization-branding';
import { isProduction, isInBrowser } from '~/constants';
import { SnackbarContextProvider } from '~c/components/base/snackbar';
import { insertIf } from '~c/helpers/spread';

import './app.scss';

const NivoThemeWrapper = lazy(() =>
  import('./base/nivo-theme-wrapper').then((m) => ({ default: m.default }))
);
const PublicPolicies = lazy(() =>
  import('./public-content/public-policies').then((m) => ({
    default: m.default,
  }))
);

/**
 * Since Navigate does not work in StaticRouter (SSR), we should
 * only do this in browser mode
 */
const Redirect: React.FC<NavigateProps> = (props) => {
  if (isInBrowser()) {
    return <Navigate replace {...props} />;
  }
  return null;
};

interface Props {
  className?: string;
}

const DARK_CANVAS_BG = '#111315';
const LIGHT_CANVAS_BG = '#F4F3F0';

/**
 * Inner app component that uses accent context to create the themed app.
 * Must be rendered inside AccentProvider (which is inside OrganizationBrandingProvider).
 */
const AppWithAccent: React.FC<Props> = ({ className }) => {
  const { effectiveMode } = useThemeMode();
  const { accentPalette } = useAccentPalette();
  const theme = React.useMemo(
    () => createAppTheme(effectiveMode, accentPalette),
    [effectiveMode, accentPalette]
  );
  const tokens = React.useMemo(
    () => getDesignTokens(effectiveMode, accentPalette),
    [effectiveMode, accentPalette]
  );
  const isDark = effectiveMode === 'dark';

  // Keep --theme-canvas-bg and document background in sync so main area and overscroll reveal match theme (avoids white flash on hard refresh and white strip in dark mode when overscrolling)
  React.useEffect(() => {
    const bg = effectiveMode === 'dark' ? DARK_CANVAS_BG : LIGHT_CANVAS_BG;
    document.documentElement.style.setProperty('--theme-canvas-bg', bg);
    document.documentElement.style.backgroundColor = bg;
    if (document.body) {
      document.body.style.backgroundColor = bg;
    }
  }, [effectiveMode]);

  const allRoutes = useRoutes([
    // PUBLIC routes
    {
      path: PUBLIC_ROUTES.signIn,
      element: <Auth.SignIn />,
    },
    {
      path: PUBLIC_ROUTES.SignUp,
      element: <Auth.SignUp />,
    },
    {
      path: PUBLIC_ROUTES.forgotPassword,
      element: <Auth.ForgotPassword />,
    },
    {
      path: PUBLIC_ROUTES.passwordCreate,
      element: <Auth.CreatePassword />,
    },
    {
      path: PUBLIC_ROUTES.confirmPasswordReset,
      element: <Auth.ConfirmResetPassword />,
    },
    {
      path: PUBLIC_ROUTES.userAccountActivation,
      element: <Auth.UserAccountActivation />,
    },
    {
      path: PUBLIC_ROUTES.userEmailVerification,
      element: <Auth.UserEmailVerification />,
    },
    {
      path: PUBLIC_ROUTES.magicLogin,
      element: <MagicLogin />,
    },
    {
      path: PUBLIC_ROUTES.acceptInvitation,
      element: <Auth.AcceptInvitation />,
    },
    {
      path: PUBLIC_ROUTES.dashboardPublic,
      element: <DashboardDemo />,
    },
    {
      path: PUBLIC_ROUTES.publicPoliciesList,
      element: (
        <Suspense fallback={null}>
          <PublicPolicies />
        </Suspense>
      ),
    },
    {
      path: PUBLIC_ROUTES.testAgent,
      element: <TestAgents />,
    },
    {
      path: PUBLIC_ROUTES.privacy,
      element: <PrivacyPolicy className={className} />,
    },
    {
      path: PUBLIC_ROUTES.termsOfUse,
      element: <PrivacyPolicy className={className} />,
    },
    // PROTECTED routes
    {
      path: '/',
      element: <Redirect to="dashboard/compliance" />,
    },
    {
      path: '/select-organization',
      element: <AuthRoute children={<Auth.OrganizationSelector />} />,
    },
    ...routes,
    ...insertIf(!isProduction(), {
      path: '/dev-demo',
      element: <AuthRoute children={<DevDemo />} />,
    }),

    // Access denied (redirected from RequireCapability)
    {
      path: '/access-denied',
      element: <AccessDeniedPage />,
    },
    // Fallthrough path
    {
      path: '*',
      element: <ErrorPage />,
    },
  ]);

  return (
    <V5MuiThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        className={className}
        sx={{
          backgroundColor: 'var(--theme-canvas-bg, #F4F3F0)',
          minHeight: '100vh',
          position: 'relative',
          // Subtle grain texture overlay for premium stone (dark) / linen (light) feel
          ...(tokens.canvasTexture !== 'none' && {
            '&::before': {
              content: '""',
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: 'none',
              backgroundImage: tokens.canvasTexture,
              opacity: isDark ? 0.03 : 0.018,
              zIndex: 0,
            },
          }),
        }}
      >
        <Suspense fallback={null}>
          <NivoThemeWrapper themeMode={effectiveMode}>
            <SnackbarContextProvider>
              <OrganizationProjectProvider>
                <PermissionsProvider>
                  <NotificationProvider>
                    <AnchProvider>
                      <NotistackProvider>
                        {allRoutes}
                        <FloatingTaskButton />
                        <SessionWarningModal />
                      </NotistackProvider>
                    </AnchProvider>
                  </NotificationProvider>
                </PermissionsProvider>
              </OrganizationProjectProvider>
            </SnackbarContextProvider>
          </NivoThemeWrapper>
        </Suspense>
      </Box>
    </V5MuiThemeProvider>
  );
};

/**
 * Wrapper that provides AccentProvider context (depends on OrganizationBrandingProvider).
 */
const AppWithAccentProvider: React.FC<Props> = (props) => (
  <AccentProvider>
    <AppWithAccent {...props} />
  </AccentProvider>
);

/**
 * Wrapper that provides OrganizationBrandingProvider (depends on SessionProvider).
 */
const AppWithBranding: React.FC<Props> = (props) => (
  <OrganizationBrandingProvider>
    <AppWithAccentProvider {...props} />
  </OrganizationBrandingProvider>
);

/**
 * Wrapper that provides SessionProvider.
 */
const AppWithSession: React.FC<Props> = (props) => (
  <SessionProvider>
    <AppWithBranding {...props} />
  </SessionProvider>
);

/**
 * Root app component that provides ThemeProvider (light/dark mode).
 */
const AppWithTheme: React.FC<Props> = (props) => (
  <ThemeProvider>
    <AppWithSession {...props} />
  </ThemeProvider>
);

export const App = AppWithTheme;
